import React, { HTMLProps, useContext, useRef, useState } from "react";
import { Grid, Theme, Typography } from "@material-ui/core";
import "./HosGraphView.css";
import { HosGraphViewCanvas } from "./HosGraphViewCanvas";
import { useTheme } from "@material-ui/styles";
import {
  Identifier,
  ReactAdminComponentProps,
  useTranslate,
} from "react-admin";
import { DriverLogDetailedView } from "../../../../common/data/service-proxies";
import { LayoutContext } from "../../../layout/LayoutContext";
import { format, parseISO } from "date-fns";

interface Props extends ReactAdminComponentProps {
  record: DriverLogDetailedView;
  onHover: (id?: Identifier) => void;
  onEventClick: (id: Identifier) => void;
  isMaster?: boolean;
}

export const HosGraphView = ({
  record,
  onHover,
  onEventClick,
  history,
  isMaster,
}: Props & HTMLProps<HTMLElement>) => {
  const svgElement = useRef<SVGSVGElement>(null);
  const theme: Theme = useTheme();
  const layoutContext = useContext(LayoutContext);
  const translate = useTranslate();
  const [hoveredViolationId, setHoveredViolationId] = useState<Identifier>();

  return (
    <Grid
      id="hos-graph-view-container"
      container
      style={{
        top: layoutContext.appBarHeight,
      }}>
      <HosGraphViewCanvas
        ref={svgElement}
        record={record}
        hoveredViolationId={hoveredViolationId}
        history={history}
        onHover={onHover}
        onEventClick={onEventClick}
        cycleBreak={record.cycleBreak}
        drawRefuels={isMaster}
      />
      {record.violations && record.violations.length > 0 ? (
        <div className="violations-display">
          {record.violations.map((violation, idx) => {
            return (
              <div
                key={idx}
                className="violation-view violation-processed"
                style={{ borderColor: theme.palette.error.main }}
                onMouseOver={() =>
                  violation.isHosViolation &&
                  setHoveredViolationId(violation.id)
                }
                onMouseLeave={() =>
                  violation.isHosViolation && setHoveredViolationId(undefined)
                }>
                <Typography
                  variant="body2"
                  style={{ color: theme.palette.error.main }}>
                  {translate(
                    `appData.violationTypes.${violation.violationError}`
                  )}
                </Typography>
                {violation.startTime && violation.endTime ? (
                  <Typography variant="caption">
                    {new Date(violation.startTime).toLocaleTimeString("en-US", {
                      timeZone: record.homeTerminalTimeZone!,
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    -{" "}
                    {new Date(violation.endTime).toLocaleTimeString("en-US", {
                      timeZone: record.homeTerminalTimeZone!,
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {isMaster && record.refuels && record.refuels.length > 0 ? (
        <div className="violations-display">
          {record.refuels.map((refuel, idx) => {
            const color = refuel.isFromProvider
              ? theme.palette.success.main
              : theme.palette.primary.main;
            const time = format(parseISO(refuel.time!), "hh:mm aa");
            return (
              <div
                key={idx}
                className="violation-view violation-processed"
                style={{ borderColor: color }}>
                <Typography variant="body2" style={{ color: color }}>
                  {refuel.isFromProvider
                    ? `Refuel reported by provider ${refuel.provider} at ${time}`
                    : `Refuel reported by ELD at ${time}`}
                </Typography>
                <Typography variant="caption">{refuel.location}</Typography>
              </div>
            );
          })}
        </div>
      ) : null}
    </Grid>
  );
};
